import React from 'react';
import classNames from 'classnames';

import { showCaseManagement } from '~support-at-home/lib/derive';

import { InputProps } from '~components/form-fields';

import { Supplement, SupplementRate } from '~lookups/models';

import { useGetLookupSupplementsQuery } from '~lookups/actions/supplements';

type LayoutType = 'inline' | 'stack';

interface BudgetSupplementsProps extends InputProps {
  layout?: LayoutType;
}

const BudgetSupplements: React.FC<BudgetSupplementsProps> = ({ value, onChange, layout = 'inline' }) => {
  const { data: supplementRates = [] } = useGetLookupSupplementsQuery();

  const supplements = Array.isArray(value.supplements) ? (value.supplements as Supplement[]) : [];

  const enabled = showCaseManagement({ package_fixed_id: value.package_fixed_id });

  const budgetSupplements = supplementRates.filter(({ type }) => type === 'budget');
  const caseManagementSupplements = supplementRates.filter(({ type }) => type === 'casemanagement');

  const isChecked = (id: string) => supplements?.some(({ supplement_id }) => supplement_id === id);

  const handleChange = (id: string) => {
    const updated = isChecked(id)
      ? supplements.filter(({ supplement_id }) => supplement_id !== id)
      : [...supplements, { supplement_id: id }];
    onChange?.('supplements', updated);
  };

  const buildBudgetSupplementLine = (supplement: SupplementRate) => {
    if (supplement.rate_type === 'per-day') {
      return `${supplement.label} - $${supplement.rate} / day`;
    }
    return `${supplement.label} - ${supplement.rate}% added to base budget`;
  };

  const containerClass = classNames({
    'd-flex gap-3': layout === 'inline',
    'd-block': layout === 'stack',
  });

  const colClass = classNames('mb-3', {
    'flex-fill': layout === 'inline',
  });

  const renderSupplementCard = (title: string, supplementList: SupplementRate[], prefix: string) => (
    <div className={colClass}>
      <div className="card shadow-sm">
        <div className="card-header bg-secondary text-white fw-bold">{title}</div>
        <ul className="list-group list-group-flush">
          {supplementList.map((supplement) => (
            <li className="list-group-item" key={supplement.id}>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`${prefix}-supplement-${supplement.id}`}
                  checked={isChecked(supplement.id)}
                  onChange={() => handleChange(supplement.id)}
                />
                <label className="form-check-label" htmlFor={`${prefix}-supplement-${supplement.id}`}>
                  {prefix === 'budget' ? buildBudgetSupplementLine(supplement) : supplement.label}
                </label>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <div className={containerClass}>
      {enabled && renderSupplementCard('Budget Supplements', budgetSupplements, 'budget')}
      {enabled &&
        renderSupplementCard(
          'Case Management Supplements ($3.95/day if any selected)',
          caseManagementSupplements,
          'cm',
        )}
      {!enabled && (
        <div className="ps-2 pe-2 pt-1 small text-danger">
          Funding type does not allow for supplements, please continue to next step
        </div>
      )}
    </div>
  );
};

export default BudgetSupplements;
